import {putAll, takeLatestF} from '@fl/cmsch-fe-library';
import {SagaIterator} from 'redux-saga';
import {isFull, none, opt} from 'ts-opt';
import {call, put, select} from 'typed-redux-saga';

import {Api} from 'api/gen/Api';
import {tCommon} from 'app/translations';
import {showBeError} from 'utils/show-be-error';
import {
    convertTableDataParamsToDto,
    getRequestedTableColumnSettings,
    getRequestedTablePageParams,
    simpleTableSelector,
    tablesAction,
} from 'utils/tables';

import {ManagementAssociationsPage} from '../../types/management-associations';
import {adminAction, GetManagementAssociationsTableDataAction} from '../action';

const tableId = 'managementAssociations';
const blankPageModel: ManagementAssociationsPage = {
    content: [],
    page: {
        number: 1,
        size: 1,
        totalElements: 1,
        totalPages: 1,
    },
};

// eslint-disable-next-line max-lines-per-function
function* execute(action: GetManagementAssociationsTableDataAction): SagaIterator {
    const {tableDataRequest, associationId} = action.payload;
    if (tableDataRequest.isEmpty) {
        return yield* put(adminAction.getManagementAssociationsTableDataSuccess(none));
    }

    const tableState = yield* select(simpleTableSelector.tableState(tableId));
    const tableDataParams = getRequestedTablePageParams({
        tableId,
        tableDataRequest: tableDataRequest.orCrash('No tableDataRequest for management associations table'),
        tableState,
        resetType: 'tableDataParams',
    });

    if (associationId.isEmpty) {
        yield* put(adminAction.getManagementAssociationsTableDataSuccess(opt(blankPageModel)));
        return yield* put(tablesAction.tableDataFetchSuccess(tableId, tableDataParams));
    }
    yield* put(tablesAction.tableDataFetchStart(tableId));

    const columnSettings = getRequestedTableColumnSettings<'managementAssociations'>(tableState);
    const {filters, params} = convertTableDataParamsToDto({tableDataParams, columnSettings});
    const correctedFilter = opt(filters)
        .mapIn(x => (x.tag === 'CONTAINS_STRING' && x.field === 'username') ? x.value : null)
        .filterIn(isFull)
        .headIn()
        .orNull();
    const correctedParams = {sort: opt(params.sort).orElse([]), usernameFilterText: correctedFilter};

    const response = yield* call(
        Api.getSubjectAssociationManagementPage,
        correctedParams,
        {associationId: associationId.orCrash('Association ID is empty')});

    if (response.isSuccess) {
        const {headers, pagedModel} = response.data;
        yield* put(adminAction.getManagementAssociationsTableDataSuccess(opt(pagedModel)));
        yield* put(adminAction.setManagementAssociationsExtraColumns(headers || []));
        yield* put(tablesAction.tableDataFetchSuccess(tableId, tableDataParams));
    } else {
        yield putAll(showBeError(response, tCommon('tableLoadErrorTitle')));
        yield* put(tablesAction.tableDataFetchFailure(tableId));
    }
}
/** Runs the admin saga that loads association-management table data. */
export function* getManagementAssociationsTableData(): SagaIterator {
    yield takeLatestF('admin/GET_MANAGEMENT_ASSOCIATIONS_TABLE_DATA', execute);
}
